<template>
  <ValidationObserver ref="observer" slim v-slot="{ invalid, handleSubmit }">
    <v-form  v-if="!mfaAuthWithMethods || isCodeSent" @submit.prevent="handleSubmit(verifyCode)">
      <v-card :loading="loading" :class="{ 'no-shadow': isMFaView }">
        <v-card-title class="px-0">
          <v-toolbar v-if="!isMFaView" class="px-0" flat dense>
            <v-toolbar-title class="primary--text">{{ $t("headers.smsAuthentication") }}</v-toolbar-title>
            <v-spacer />
            <v-btn id="dismiss" icon @click="dismiss">
              <v-icon color="primary">$close</v-icon>
            </v-btn>
          </v-toolbar>
        </v-card-title>
        <v-card-text class="black--text">
          <p v-if="isMFaView" class="font-weight-bold mb-4 text-h5">{{ $t("headers.smsAuthentication") }}</p>
          <strong v-if="isCodeSent">{{ maskedNum }}</strong>
          <p>{{ !isCodeSent ? $t("messages.mfaEnterSmsDesc") : $t("messages.codeResend") }}</p>
          <p v-if="isCodeSent && !canResendCode" class="text-center">
            {{ $t("messages.resendSec", { seconds: secondsRemaining }) }}
          </p>
          <div class="text-center mb-4">
            <v-btn
              v-if="isCodeSent"
              type="button"
              color="primary"
              class="mx-auto"
              :disabled="!canResendCode"
              @click.stop.prevent="onResendClick"
              >{{ $t("captions.resend") }}
            </v-btn>
          </div>
          <VTelephoneNumberWithValidation
            v-if="!isCodeSent"
            v-model="phone"
            rules="required"
            @numberVerified="isNumberVerified = true"
            @numberInvalid="isNumberVerified = false"
            :showHint="false"
            :showStaticError="true"
            label="enterNumber"
            placeholder="enterNumber"
          />
          <v-text-field-with-validation
            v-else
            v-model="authCode"
            rules="required"
            label="enterCode"
            :numberOnly="true"
            placeholder="enterCode"
            maxlength="6"
            autocomplete="off"
            :showCustomRequiredError="true"
          />
          <div class="d-flex justify-center">
            <error-label
              class="mb-2"
              v-model="showApiError"
              :message="errorMessage"
              ref="errorLabel"
            />
          </div>
          <p class="mt-4 mb-0" v-if="!isCodeSent">{{ $t("messages.codeValidation") }}</p>
          <p class="mt-4 mb-0" v-if="resendButtonClicked">{{ $t("messages.smsCodeSendDesc") }}</p>
        </v-card-text>
        <v-card-actions class="d-flex flex-column pb-8 justify-center align-center">
          <v-btn
            v-if="!isCodeSent"
            type="button"
            color="primary"
            :disabled="invalid || !isNumberVerified"
            @click="sendCode"
          >
            {{ $t("captions.sendCode") }}
          </v-btn>
          <v-btn v-else type="button" color="primary" :disabled="invalid || invalidCodeLength" @click="verifyCode">
            {{ $t("captions.verify") }}
          </v-btn>
          <v-btn v-if="mfaAuthWithMethods" class="my-6 mx-auto" color="primary" @click="goToAuthenticationWindow">
            {{ $t("captions.tryAnotherMethod") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
    <div class="text-center d-flex justify-center align-center error-container" v-else>
      <v-progress-circular v-if="loading" class="my-2 mt-10" indeterminate size="54"></v-progress-circular>
      <error-label
        class="mb-2 d-inline-block"
        v-model="showApiError"
        :message="errorMessage"
        :timeout="5000"
        ref="errorLabel"
      ></error-label>
    </div>

  </ValidationObserver>
</template>

<script lang="ts">
import Vue from "vue";
import { ValidationObserver } from "vee-validate";
import { AuthService, mfaTypeEnum } from "@/service";

import VTextFieldWithValidation from "../form/VTextFieldWithValidation.vue";
import VTelephoneNumberWithValidation from "../form/VTelephoneNumberWithValidation.vue";
import ErrorLabel from "@/components/objects/ErrorLabel.vue";
import { parseApiError } from "@/plugins/helper";

import { RootStoreInterface } from "@/store/types";
import { createHelpers } from "vuex-typescript-interface";

const { mapGetters, mapMutations } = createHelpers<RootStoreInterface>();

const getters = mapGetters(["user", "getMfaPhone", "getMfaUsedHit"]);

const setters = mapMutations(["setLoginSecurityConfirmDlg", "setMfaUsedHit"]);


export default Vue.extend({
  components: {
    ValidationObserver,
    VTextFieldWithValidation,
    VTelephoneNumberWithValidation,
    ErrorLabel,
  },
  data: () => ({
    loading: false,
    phone: "",
    isCodeSent: false,
    authCode: "",
    canResendCode: false,
    secondsRemaining: 60,
    resendTimer: null as ReturnType<typeof setInterval> | null,
    showApiError: false,
    errorMessage: "",
    isNumberVerified: false,
    resendButtonClicked: false,
  }),
  props: {
    isParentVisible: {
      type: Boolean,
      default: false,
    },
    hasMfaMethods: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...getters,
    maskedNum(): string {
      const countryCode = this.phone[1] == "1" ? this.phone[1] : "1"; // set/force default to US only
      const last4 = this.phone.slice(-4);

      return `Phone: +${countryCode} (***)-***-${last4}`;
    },
    isMFaView(): boolean {
      return this.$route.path === "/mfa" || this.$route.name === "mfaComponent";
    },
    isAccountView(): boolean {
      return this.$route.path === "/account" || this.$route.name === "Account"
    },
    mfaAuthWithMethods(): boolean {
      return this.hasMfaMethods && this.isMFaView;
    },
    invalidCodeLength(): boolean  {
      return this.authCode.length != 6;
    },
     isHitThroughLogin(): boolean {
      return this.getMfaUsedHit;
    }
  },
  methods: {
    ...setters,
    dismiss() {
      this.$emit("dismiss");
    },
    showError(msg: string) {
      this.errorMessage = msg;
      this.showApiError = true;
    },
    async sendCode() {
      try {
        this.loading = true;
        this.setLoginSecurityConfirmDlg(true);

        await AuthService.getMfasmsInit({ phone: this.phone });

        this.isCodeSent = true;

        this.startResendCountdown();
      } catch (e: any) {
        console.log("error sending SMS OTP:", e);

        this.showError(parseApiError(e as any));
      }
      this.loading = false;
    },
    async sendCodeVerfiedNum() {
      try {
        this.loading = true;
        // this.setLoginSecurityConfirmDlg(true);

        this.phone = this.getMfaPhone ?? "";

        (!this.isHitThroughLogin) ? await AuthService.getMfaRequestCode({mfaType: mfaTypeEnum.SMS}) : this.setMfaUsedHit(false);;

        this.isCodeSent = true;

        this.startResendCountdown();
      } catch (e: any) {
        console.log("error sending SMS OTP to verified Num:", e);

        this.showError(parseApiError(e as any));
      }
      this.loading = false;
    },
    async verifyCode() {
       if (this.invalidCodeLength) return;

      try {
        this.loading = true;
        const payload = {
          authCode: this.authCode,
          mfaType: mfaTypeEnum.SMS,
        };

        const res = await AuthService.getMfaVerify(payload);
        const result = String(res.result || "").trim().toLowerCase();
        if (
          result === "success" ||
          result === "tenantselectionrequired" ||
          result.startsWith("theme:")
        ) {
          this.$emit("mfa-verified", res.result);
          return;
        }
        this.showError("Invalid code entered. Please try again.");
      } catch (e: any) {
        const maxLimitReached = e.status == 401;
        const customError = (!this.isMFaView || maxLimitReached) ? parseApiError(e as any) : "Invalid code entered. Please try again.";
        this.showError(customError);
      } finally {
        this.loading = false;
      }
    },
    onResendClick() {
      this.resetInitValues();
      this.mfaAuthWithMethods ? this.sendCodeVerfiedNum() : this.sendCode();
      this.resendButtonClicked = true;
    },
    startResendCountdown() {
      this.secondsRemaining = 60;
      this.canResendCode = false;
      if (this.resendTimer) clearInterval(this.resendTimer);

      this.resendTimer = setInterval(() => {
        if (this.secondsRemaining > 0) {
          this.secondsRemaining--;
        } else {
          this.canResendCode = true;
          clearInterval(this.resendTimer!);
          this.resendTimer = null;
        }
      }, 1000);
    },
    resetInitValues() {
      this.phone = "";
      this.isCodeSent = false;
      this.authCode = "";
      this.canResendCode = false;
      if (this.resendTimer) clearInterval(this.resendTimer);
      this.resendTimer = null;
      this.showApiError = false;
      this.errorMessage = "";
      this.isNumberVerified = false;

      this.$refs.observer?.reset();
      // Clear api error
    },
    goToAuthenticationWindow() {
      this.setMfaUsedHit(false);
      this.$eventBus.$emit("hideAppBarBtn");
    },
  },
  watch: {
    isParentVisible(newVal: boolean) {
      if (newVal) {
        this.resetInitValues();
      }
    },
  },

  mounted() {
    if(this.mfaAuthWithMethods) this.sendCodeVerfiedNum();
  },
  beforeDestroy() {
    if (this.resendTimer) clearInterval(this.resendTimer);
  },
});
</script>
<style lang="scss" scoped>
.v-card.no-shadow {
  box-shadow: none;
}

.error-container {
  height: 400px;

  &:empty {
    height: 200px;
  }
}
</style>
